import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, SaveOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getCvApplicationDetails, updateCvApplication } from '../../../../services/api';

const APPLICATION_STATUS_OPTIONS = [
  { value: 'in_progress', label: 'Em andamento', color: 'info' },
  { value: 'passed', label: 'Aprovado', color: 'success' },
  { value: 'rejected', label: 'Reprovado', color: 'error' },
  { value: 'hired', label: 'Contratado', color: 'success' },
];

const applicationStatusMap = Object.fromEntries(
  APPLICATION_STATUS_OPTIONS.map((item) => [item.value, item]),
);

const decisionStatusMap = {
  pass: { label: 'Aprovado', color: 'success' },
  fail: { label: 'Reprovado', color: 'error' },
  manual: { label: 'Em andamento', color: 'warning' },
  pending_adjust: { label: 'Em andamento', color: 'warning' },
  default: { label: 'Em andamento', color: 'info' },
};

const formatDate = (dateString) => {
  if (!dateString) return 'Atual';
  try {
    const date = new Date(`${dateString}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  } catch {
    return dateString;
  }
};

const formatDateTime = (value) => {
  if (!value) return 'Não informado';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Não informado';
  return date.toLocaleString('pt-BR');
};

const RenderAnswer = ({ answer }) => {
  const valueText = answer?.value_text;

  try {
    if (valueText && typeof valueText === 'string' && valueText.trim().startsWith('[')) {
      const experiences = JSON.parse(valueText);
      if (Array.isArray(experiences)) {
        return (
          <Stack spacing={1.25} sx={{ mt: 1 }}>
            {experiences.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Nenhuma experiência informada.
              </Typography>
            )}

            {experiences.map((exp, index) => (
              <Paper key={`${exp?.company_name || 'exp'}-${index}`} variant="outlined" sx={{ p: 1.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {exp?.company_name || 'Empresa não informada'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(exp?.start_date)} até {formatDate(exp?.end_date)}
                </Typography>
              </Paper>
            ))}
          </Stack>
        );
      }
    }
  } catch {
    // fallback para renderizacao simples abaixo
  }

  const textValue = answer?.value_option_label
    || (answer?.value_boolean === true ? 'Sim' : answer?.value_boolean === false ? 'Não' : null)
    || answer?.value_number
    || answer?.value_text;

  if (!textValue) {
    return (
      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.disabled' }}>
        Não respondido
      </Typography>
    );
  }

  return (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
      {textValue}
    </Typography>
  );
};

export default function ApplicationDetails({ applicationId, onBack }) {
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');

  const loadDetails = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCvApplicationDetails(applicationId);
      setApplication(data || null);
      setSelectedStatus(data?.status || 'in_progress');
    } catch {
      toast.error('Erro ao carregar detalhes da candidatura.');
      setApplication(null);
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    if (!applicationId) {
      setApplication(null);
      setLoading(false);
      return;
    }

    void loadDetails();
  }, [applicationId, loadDetails]);

  const handleSaveStatus = async () => {
    if (!application?.id || !selectedStatus || selectedStatus === application?.status) return;

    setSavingStatus(true);
    try {
      await updateCvApplication(application.id, { status: selectedStatus });
      setApplication((prev) => (prev ? { ...prev, status: selectedStatus } : prev));
      toast.success('Status atualizado com sucesso!');
    } catch {
      toast.error('Não foi possível atualizar o status da candidatura.');
    } finally {
      setSavingStatus(false);
    }
  };

  const candidate = application?.candidate || {};
  const job = application?.job || {};
  const responses = Array.isArray(application?.responses) ? application.responses : [];

  const appStatusInfo = applicationStatusMap[application?.status] || {
    label: application?.status || 'Sem status',
    color: 'default',
  };

  const canSaveStatus = Boolean(
    application?.id
      && selectedStatus
      && selectedStatus !== application?.status
      && !savingStatus,
  );

  const responsesCountLabel = useMemo(
    () => `${responses.length} ${responses.length === 1 ? 'etapa respondida' : 'etapas respondidas'}`,
    [responses.length],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!application) {
    return (
      <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
        <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }}>
          Voltar para candidaturas
        </Button>
        <Alert severity="warning">
          Não foi possível carregar os dados da candidatura ou ela não foi encontrada.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      <Stack spacing={2}>
        <Button
          startIcon={<ArrowBack />}
          onClick={onBack}
          sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}
        >
          Voltar para candidaturas
        </Button>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 1.25,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            background: 'linear-gradient(135deg, #f8fbff 0%, #f2f7ff 100%)',
          }}
        >
          <Stack spacing={2.25}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2}
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {candidate?.full_name || 'Candidato sem nome'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Vaga: {job?.title || 'Vaga não informada'}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
                  <Chip size="small" label={appStatusInfo.label} color={appStatusInfo.color} />
                  <Chip size="small" variant="outlined" label={responsesCountLabel} />
                </Stack>
              </Box>

              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  width: { xs: '100%', md: 320 },
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                }}
              >
                <Stack spacing={1.25}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Atualizar status
                  </Typography>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Status da candidatura"
                    value={selectedStatus}
                    onChange={(event) => setSelectedStatus(event.target.value)}
                  >
                    {APPLICATION_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Button
                    variant="contained"
                    startIcon={savingStatus ? <CircularProgress size={16} color="inherit" /> : <SaveOutlined />}
                    onClick={handleSaveStatus}
                    disabled={!canSaveStatus}
                    sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
                  >
                    {savingStatus ? 'Salvando...' : 'Salvar status'}
                  </Button>
                </Stack>
              </Paper>
            </Stack>

            <Divider />

            <Grid container spacing={1.25}>
              <Grid item xs={12} sm={6} md={4}>
                <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {candidate?.email || 'Não informado'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Telefone
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {candidate?.phone || 'Não informado'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Data da aplicação
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatDateTime(application?.created_at)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: 1,
            border: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Respostas por etapa
            </Typography>
            <Chip size="small" variant="outlined" label={responsesCountLabel} />
          </Stack>

          {responses.length > 0 ? (
            <Stack spacing={1.5}>
              {responses.map((response, index) => {
                const decisionInfo = decisionStatusMap[response?.decision] || decisionStatusMap.default;
                const answers = Array.isArray(response?.answers) ? response.answers : [];

                return (
                  <Paper key={response?.id || `response-${index}`} variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Stack spacing={1.5}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {response?.stage_name || `Etapa ${index + 1}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Respondido em {formatDateTime(response?.created_at)}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={`Decisão: ${decisionInfo.label}`}
                          color={decisionInfo.color}
                        />
                      </Stack>

                      <Divider />

                      {answers.length > 0 ? (
                        <Stack spacing={1.25}>
                          {answers.map((answer, answerIndex) => (
                            <Box
                              key={answer?.id || `${response?.id || index}-answer-${answerIndex}`}
                              sx={{
                                p: 1.25,
                                borderRadius: 1,
                                border: (theme) => `1px solid ${theme.palette.divider}`,
                                bgcolor: 'background.default',
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}
                              >
                                {answer?.question_label || 'Pergunta'}
                              </Typography>
                              <RenderAnswer answer={answer} />
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Nenhuma resposta registrada para esta etapa.
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Nenhuma resposta submetida ainda.
              </Typography>
            </Paper>
          )}
        </Paper>
      </Stack>
    </Box>
  );
}
